import { apiUrl } from "../components/ApiManager/AxiosHeaders";
import { getApiErrorMessage } from "./apiErrorMessage";

const CATALOG_STORAGE_KEY = "cms.invoiceCatalog.v1";
const CATALOG_TTL_MS = 5 * 60 * 1000;

function unwrapData(payload) {
  if (payload?.data && typeof payload.data === "object") return payload.data;
  return payload;
}

function readCachedCatalog() {
  try {
    const raw = sessionStorage.getItem(CATALOG_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.data || !parsed.savedAt) return null;
    if (Date.now() - parsed.savedAt > CATALOG_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function writeCachedCatalog(data) {
  try {
    sessionStorage.setItem(
      CATALOG_STORAGE_KEY,
      JSON.stringify({ savedAt: Date.now(), data }),
    );
  } catch {
    // ignore quota / private mode
  }
}

export function invalidateInvoiceCatalog() {
  try {
    sessionStorage.removeItem(CATALOG_STORAGE_KEY);
  } catch {
    // ignore quota / private mode
  }
}

let catalogInflight = null;

export async function fetchInvoiceCatalog({ force = false } = {}) {
  if (!force) {
    const cached = readCachedCatalog();
    if (cached) return cached;
    if (catalogInflight) return catalogInflight;
  }

  const request = (async () => {
    const response = await apiUrl.get("application-invoices/catalog");
    const data = unwrapData(response?.data);
    if (data && typeof data === "object" && !Array.isArray(data)) {
      writeCachedCatalog(data);
    }
    return data || {};
  })();

  catalogInflight = request;
  try {
    return await request;
  } finally {
    if (catalogInflight === request) {
      catalogInflight = null;
    }
  }
}

export async function fetchApplicationInvoiceCounts(params = {}, { signal } = {}) {
  const response = await apiUrl.get("application-invoices/counts", {
    params,
    signal,
  });
  return unwrapData(response?.data) || {};
}

export async function fetchCommissionInvoiceCounts(params = {}, { signal } = {}) {
  const response = await apiUrl.get("commission-invoices/counts", {
    params,
    signal,
  });
  return unwrapData(response?.data) || {};
}

export async function fetchApplicationInvoiceDetail({
  invoiceId,
  clientId,
  signal,
} = {}) {
  const path = invoiceId
    ? `application-invoices/${invoiceId}`
    : "application-invoices/new";
  const response = await apiUrl.get(path, {
    params: invoiceId ? undefined : { client_id: clientId },
    signal,
  });
  const payload = response?.data;
  if (payload?.success === false || payload?.status === false) {
    throw new Error(payload?.message || "Failed to load invoice details.");
  }
  const data = unwrapData(payload) || {};
  return data;
}

export async function fetchCommissionInvoiceDetail({
  invoiceId,
  clientId,
  planId,
  signal,
} = {}) {
  const path = invoiceId
    ? `commission-invoices/${invoiceId}`
    : "commission-invoices/new";
  const params = invoiceId
    ? undefined
    : { client_id: clientId, plan_id: planId || undefined };
  const response = await apiUrl.get(path, { params, signal });
  const payload = response?.data;
  if (payload?.success === false || payload?.status === false) {
    throw new Error(
      payload?.message || "Failed to load commission invoice details.",
    );
  }
  const data = unwrapData(payload) || {};
  return data;
}

export async function fetchInvoiceNoteList(invoiceId, { module = "application", signal } = {}) {
  if (!invoiceId) return [];
  const path =
    module === "commission"
      ? `commission-invoices/${invoiceId}/notes`
      : `application-invoices/${invoiceId}/notes`;
  const response = await apiUrl.get(path, { signal });
  const payload = response?.data;
  if (payload?.success === false || payload?.status === false) {
    throw new Error(payload?.message || "Failed to load invoice notes.");
  }
  const data = unwrapData(payload);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.notes)) return data.notes;
  return [];
}

export function workspaceErrorMessage(error, fallback) {
  if (
    error?.code === "ERR_CANCELED" ||
    error?.name === "CanceledError" ||
    error?.message === "canceled"
  ) {
    return null;
  }
  return getApiErrorMessage(error) || fallback;
}

export function applyInvoiceCatalog(catalog = {}) {
  return {
    setting: catalog.setting || null,
    discountRules: Array.isArray(catalog.discount_rules)
      ? catalog.discount_rules
      : [],
    gstSettings: Array.isArray(catalog.gst_settings)
      ? catalog.gst_settings
      : [],
    branches: Array.isArray(catalog.branches) ? catalog.branches : [],
    brandings: Array.isArray(catalog.brandings) ? catalog.brandings : [],
    followupUsers: Array.isArray(catalog.followup_users)
      ? catalog.followup_users
      : [],
  };
}

export function wrapCommissionWorkspaceInvoice(invoice) {
  if (!invoice) return null;
  const commissionInstallmentIds =
    invoice.commission_installment_ids || invoice.installment_ids || [];
  const bonusInstallmentIds = invoice.bonus_installment_ids || [];
  return {
    invoice: {
      ...invoice,
      commissionInstallmentIds,
      bonusInstallmentIds,
      invoiceSourceId: invoice.invoice_source_id,
      invoiceNumber: invoice.invoice_number,
      invoiceDate: invoice.issue_date || invoice.invoice_date,
      dueDate: invoice.due_date,
    },
    raw: invoice,
    notes: Array.isArray(invoice.notes) ? invoice.notes : [],
    commissionInstallmentIds,
    bonusInstallmentIds,
    linkedInstallments: invoice.linked_installments || [],
    linkedBonusInstallments: invoice.linked_bonus_installments || [],
  };
}

export function mapWorkspacePlanInvoiceIndex(rows = []) {
  return (Array.isArray(rows) ? rows : []).map((row) => ({
    id: row.id,
    invoiceNumber: row.invoice_number || row.invoiceNumber || "",
    invoice_number: row.invoice_number || row.invoiceNumber || "",
    financeInvoiceId: row.id,
    commissionInstallmentIds: (
      row.commission_installment_ids ||
      row.commissionInstallmentIds ||
      []
    )
      .map(String)
      .filter(Boolean),
    bonusInstallmentIds: (
      row.bonus_installment_ids ||
      row.bonusInstallmentIds ||
      []
    )
      .map(String)
      .filter(Boolean),
  }));
}

export function suggestionViewModelFromWorkspaceApplication(application) {
  if (!application || typeof application !== "object") return {};
  const course = application.course || {};
  const provider = application.provider || {};
  const client = application.client || {};
  return {
    course: {
      ...course,
      id: application.course_id || course.id,
      name: course.name,
      startDate: course.startDate || course.startdate || application.start_date,
      endDate: course.endDate || course.enddate || application.end_date,
      applicationCourseId:
        application.application_course_id || course.application_course_id,
    },
    provider: {
      ...provider,
      id: application.provider_id || provider.id,
      name: provider.name,
    },
    student: {
      name: client.name || client.full_name || "",
      studentId: client.student_id || "",
    },
    application: {
      id: application.application_id,
      applicationCourseId: application.application_course_id,
      type: "education",
    },
  };
}

export function planPayloadFromWorkspace(workspace = {}) {
  return {
    plan: workspace.plan || null,
    installments: Array.isArray(workspace.installments)
      ? workspace.installments
      : [],
    bonuses: Array.isArray(workspace.bonuses) ? workspace.bonuses : [],
    raw: workspace.plan || null,
  };
}
